import React, { useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, IconButton, InputBase, MenuItem, Select, Tooltip, useTheme } from '@mui/material';
import type { InputBaseProps } from '@mui/material';
import { fontWeights } from '../../theme/typography';
import { calendarUiPalette } from './calendarPalette';
import CalendarSearchResultRow from './CalendarSearchResultRow';
import { CalendarEvent } from './types';

interface CalendarQuickSearchAutocompleteProps {
  searchCategoryOptions: readonly string[];
  searchCategory: string;
  searchValue: string;
  searchResults: CalendarEvent[];
  isSearchLoading: boolean;
  onSearchCategoryChange: (value: string) => void;
  onSearchValueChange: (value: string) => void;
  onSearchClear: () => void;
  onResultSelect: (event: CalendarEvent) => void;
}

export default function CalendarQuickSearchAutocomplete({
  searchCategoryOptions,
  searchCategory,
  searchValue,
  searchResults,
  isSearchLoading,
  onSearchCategoryChange,
  onSearchValueChange,
  onSearchClear,
  onResultSelect,
}: Readonly<CalendarQuickSearchAutocompleteProps>) {
  const theme = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchControlTypography = {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: '1rem',
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.2,
    letterSpacing: '0.01em',
  } as const;
  const searchPlaceholderTypography = {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: '14px',
    fontWeight: fontWeights.regular,
    lineHeight: 1.2,
    letterSpacing: '0.01em',
  } as const;
  const searchTooltipCopy = {
    category: 'Choose which field the quick search should match before you type.',
    input: 'Type at least 2 characters to list matching entries below the search bar.',
    clear: 'Clear the current search text and close the results list.',
  } as const;

  const handleSearchInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
    if (reason === 'input') {
      onSearchValueChange(value);
      setDropdownOpen(value.trim().length >= 2);
    }
    if (reason === 'clear') {
      onSearchClear();
      setDropdownOpen(false);
    }
  };

  const handleSearchOpen = () => {
    if (searchValue.trim().length >= 2) {
      setDropdownOpen(true);
    }
  };

  const handleSearchClose = (_event: React.SyntheticEvent, reason: string) => {
    if (reason !== 'selectOption') {
      setDropdownOpen(false);
    }
  };

  const handleOptionSelect = (_event: React.SyntheticEvent, value: CalendarEvent | string | null) => {
    if (value && typeof value === 'object') {
      onResultSelect(value);
      setDropdownOpen(false);
    }
  };

  const handleSearchClear = () => {
    onSearchClear();
    setDropdownOpen(false);
  };

  return (
    <Autocomplete
      freeSolo
      open={dropdownOpen}
      onOpen={handleSearchOpen}
      onClose={handleSearchClose}
      options={searchResults}
      loading={isSearchLoading}
      loadingText="Searching..."
      noOptionsText={searchValue.trim().length >= 2 ? 'No matching events' : ''}
      filterOptions={(x) => x}
      inputValue={searchValue}
      onChange={handleOptionSelect}
      onInputChange={handleSearchInputChange}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.primaryTitle)}
      isOptionEqualToValue={(option, value) => option.id === value.id && option.logtype === value.logtype}
      renderOption={(props, option) => (
        <li {...props} key={`${option.id}-${option.logtype}`}>
          <CalendarSearchResultRow event={option} />
        </li>
      )}
      sx={{
        height: 32,
        minWidth: 0,
        flex: '0 1 520px',
        '& .MuiAutocomplete-endAdornment': {
          display: 'none',
        },
        '& .MuiAutocomplete-listbox': {
          py: 0.5,
        },
        '& .MuiAutocomplete-loading': {
          ...searchControlTypography,
          fontSize: '14px',
          color: calendarUiPalette.topBarControls.buttonText,
        },
      }}
      renderInput={(params) => (
        <Box
          ref={params.InputProps.ref}
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'stretch',
            overflow: 'hidden',
            border: `1px solid ${calendarUiPalette.topBarControls.fieldBorder}`,
            borderRadius: '4px',
            backgroundColor: calendarUiPalette.topBarControls.surfaceBackground,
          }}
        >
          <Box
            sx={{
              height: 32,
              flexShrink: 0,
              borderRight: `1px solid ${calendarUiPalette.topBarControls.fieldBorder}`,
              backgroundColor: calendarUiPalette.topBarControls.surfaceBackground,
              display: 'flex',
              alignItems: 'center',
              '& .MuiInputBase-root': {
                height: '100%',
                width: '100%',
              },
              '& .MuiSelect-select': {
                height: '100%',
                px: 2.25,
                py: 0,
                display: 'flex',
                alignItems: 'center',
                ...searchControlTypography,
                color: calendarUiPalette.topBarControls.buttonText,
                boxSizing: 'border-box',
              },
              '& .MuiSelect-icon': {
                color: calendarUiPalette.categoryBar.unselectedIconForeground,
              },
            }}
          >
            <Tooltip title={searchTooltipCopy.category} placement="top" enterDelay={500}>
              <Select
                value={searchCategory}
                onChange={(event) => onSearchCategoryChange(event.target.value)}
                size="small"
                input={<InputBase />}
                data-cy="calendar-search-category-select"
                sx={{
                  width: 'auto',
                  minWidth: 0,
                  '& .MuiSelect-select': {
                    pr: '34px !important',
                  },
                }}
              >
                {searchCategoryOptions.map((option) => (
                  <MenuItem key={option} value={option} sx={searchPlaceholderTypography}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Tooltip>
          </Box>
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip title={searchTooltipCopy.input} placement="top" enterDelay={500}>
              <InputBase
                {...(params.inputProps as InputBaseProps)}
                autoComplete="off"
                placeholder={`Search ${searchCategory} for keywords...`}
                data-cy="calendar-quick-search-input"
                sx={{
                  flex: 1,
                  height: '100%',
                  '& .MuiInputBase-input': {
                    height: '100%',
                    padding: '0 12px 0 14px',
                    boxSizing: 'border-box',
                    ...searchPlaceholderTypography,
                    color: calendarUiPalette.topBarControls.buttonText,
                    '&::placeholder': {
                      fontSize: '14px',
                    },
                  },
                }}
              />
            </Tooltip>
            {searchValue ? (
              <Tooltip title={searchTooltipCopy.clear} placement="top" enterDelay={500}>
                <IconButton size="small" onClick={handleSearchClear} data-cy="calendar-quick-search-clear-btn">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Box
              component="span"
              sx={{
                height: '32px',
                width: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                borderLeft: `1px solid ${calendarUiPalette.topBarControls.fieldBorder}`,
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
                backgroundColor: calendarUiPalette.topBarControls.searchActionBackground,
                color: calendarUiPalette.topBarControls.searchActionIcon,
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
          </Box>
        </Box>
      )}
    />
  );
}
